<script setup lang="ts">
import { useI18n } from "vue-i18n";

import moment from "moment-timezone";

import { capitalize } from "@/helpers/string";

import type { IRegularAvailabilities } from '@/types/worker';

import AvailabilityRow from "./AvailabilityRow.vue";

const { t } = useI18n();
interface AvailabilityTableProps {
    regularAvailabilities: Array<IRegularAvailabilities>;
    edition?: boolean;
    compactColumnLabels?: boolean;
}

const props = withDefaults(defineProps<AvailabilityTableProps>(), {
    edition: false,
    compactColumnLabels: false,
});

const emit = defineEmits(["input"]);

const handleChange = (value: IRegularAvailabilities, index: number) => {
    emit(
        "input",
        props.regularAvailabilities.map((item, i) => {
            return index === i ? value : item;
        })
    );
};

const handleColumnToggle = (slot: keyof IRegularAvailabilities) => {
    const isAllChecked = props.regularAvailabilities.every((a) => a[slot]);
    const newValue = !isAllChecked;
    emit(
        "input",
        props.regularAvailabilities.map((a) => ({ ...a, [slot]: newValue }))
    );
};

const days = moment.weekdays(true);

const dayTimes: { label: keyof IRegularAvailabilities; icon: string }[] = [
    { label: "morning", icon: "sunrise" },
    { label: "afternoon", icon: "sun" },
    { label: "evening", icon: "sunset" },
    { label: "night", icon: "moon" },
];
</script>
<template>
    <div class="availability-table" :class="{ 'availability-table--figma': compactColumnLabels }">
        <template v-if="compactColumnLabels">
            <div class="availability-table__figma-card">
                <div class="availability-table__figma-header">
                    <div class="availability-table__figma-corner" aria-hidden="true" />
                    <div
                        v-for="{ label } in dayTimes"
                        :key="`avail-head-${label}`"
                        class="availability-table__figma-header-cell typo-xxs-medium"
                        :class="{ 'availability-table__figma-header-cell--clickable': edition }"
                        @click="edition && handleColumnToggle(label)"
                    >
                        {{ t(`page.interim.regularAvailabilities.columnHeadersShort.${label}`) }}
                    </div>
                </div>
                <div class="availability-table__figma-body">
                    <AvailabilityRow
                        v-for="(day, index) in days"
                        :key="index"
                        :availability="regularAvailabilities[index]"
                        :edition="edition"
                        :day="day"
                        compact
                        @input="(value: IRegularAvailabilities) => handleChange(value, index)"
                    />
                </div>
            </div>
        </template>
        <template v-else>
            <div class="day-moment">
                <div
                    v-for="{ label, icon } in dayTimes"
                    :key="label"
                    class="moment"
                    :class="{ 'moment--clickable': edition }"
                    @click="edition && handleColumnToggle(label)"
                >
                    <i :class="['ti', `ti-${icon}`]"></i>
                    <span>{{ capitalize(t(`global.dayTime.${label}`)) }}</span>
                </div>
            </div>
            <AvailabilityRow
                v-for="(day, index) in days"
                :key="index"
                :availability="regularAvailabilities[index]"
                :edition="edition"
                :day="day"
                @input="(value: IRegularAvailabilities) => handleChange(value, index)"
            />
        </template>
    </div>
</template>
<style scoped lang="scss">
.availability-table {
    .day-moment {
        display: grid;
        grid-template-columns: repeat(5, 1fr);

        .moment {
            gap: 4px;
            display: flex;
            grid-column: auto / span 1;
            align-items: center;
            flex-direction: column;
            padding: 8px 0;
            color: $text-sub-600;
            font-weight: 500;

            &--clickable {
                cursor: pointer;
                user-select: none;
            }

            .ti {
                color: $icon-strong-900;
                font-size: 20px;
            }

            &:first-child {
                grid-column-start: 2;
            }

            span {
                color: $text-sub-600;
            }
        }
    }

    &--figma {
        width: 100%;
    }

    &__figma-card {
        display: flex;
        flex-direction: column;
        width: 100%;
        padding: var(--spacing-md, 8px);
        border-radius: var(--radius-xl, 12px);
        border: 1px solid var(--stroke-soft-200, #dbdfe6);
        background-color: var(--bg-white-0, #ffffff);
        box-shadow: var(--regular-shadow-xs, 0px 1px 2px #dbdfe640);
    }

    &__figma-header {
        display: flex;
        align-items: stretch;
        padding: 0 var(--spacing-md, 8px);
        border-bottom: 1px solid var(--stroke-soft-200, #dbdfe6);
        min-height: 28px;
    }

    &__figma-corner {
        flex: 0 0 128px;
        width: 128px;
        min-width: 128px;
    }

    &__figma-header-cell {
        display: flex;
        flex: 1 1 0;
        align-items: center;
        justify-content: center;
        min-width: 0;
        padding: 0 var(--spacing-xs, 4px);
        color: var(--text-main-900, #00061a);

        &--clickable {
            cursor: pointer;
            user-select: none;
        }
    }

    &__figma-body {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-xxs, 2px);
        margin-top: var(--spacing-xxs, 4px);
    }
}
</style>
